import React, { FC, useState, useEffect } from 'react';
import { Icon, SelectionMode, DetailsListLayoutMode, OverflowSet, CommandBarButton, IOverflowSetItemProps } from '@fluentui/react';
import { Dialog, DetailsList, ColumnInfo, Language } from '@eta/components';
import { useTranslation } from 'react-i18next';
import { ApplicationDto, ApplicationStatuses } from '../Service/proxy';
import { UIHelpers } from '../../helpers/UIHelpers';
import { FileHelpers } from '../../helpers/FileHelper';
import { ServiceCreator } from '../Service/client';

export interface Props {
    hidden: boolean;
    onClose: () => void;
    taxpayerId: number|undefined;
    items: ApplicationDto[] | undefined;
    currentLanguage: Language;
}

export const ApplicationHistory: FC<Props> = (
    props: Props
) => {

    const {
        hidden,
        onClose,
        taxpayerId,
        items: itemsProps,
        currentLanguage
    } = props;
    const { t } = useTranslation(['intermediaries', 'common']);
    const [columns, setColumns] = useState<ColumnInfo[]>();
    const [items, setItems] = useState<ApplicationDto[]>();

    useEffect(() => {
        setItems(itemsProps?.slice());
    }, [itemsProps]);

    const getClassNameColorAndText = (
        status: ApplicationStatuses | undefined
    ) => {
        let color: string | undefined;
        let text: string | undefined;
        switch (status) {
            case ApplicationStatuses.Approved:
                color = 'green';
                text = t('Approved');
                break;
            case ApplicationStatuses.Pending:
                color = 'orange';
                text = t('Pending');
                break;
            case ApplicationStatuses.Rejected:
                color = 'red';
                text = t('Rejected');
                break;
            default:
                color = 'black';
                text = t('None');
        }
        return [color, text];
    };
    const onRenderItemStyles = {
        root: { padding: '10px' },
        menuIcon: { fontSize: '16px' },
    };
    const onRenderOverflowButtonStyles = {
        root: { padding: '10px' },
        menuIcon: { fontSize: '16px' },
    };
    const onRenderItem = ({ name,size, icon, onClick }: IOverflowSetItemProps): JSX.Element => {
        return (
            <>
            <CommandBarButton
                role="menuitem"
                aria-label={name}
                
                styles={onRenderItemStyles}
                iconProps={{ iconName: icon }}
                onClick={onClick}
            />
            <span>{size}</span>
            </>
        );
    };
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const onRenderOverflowButton = (overflowItems?: any[]): JSX.Element => {
        return (
            <CommandBarButton
                role="menuitem"
                title="More items"
                styles={onRenderOverflowButtonStyles}
                menuIconProps={{ iconName: 'MoreVertical' }}
                menuProps={{ items: overflowItems ?? [] }}
            />
        );
    };
    useEffect(() => {
        const gridColumns = [
            {
                key: 'submissionDate',
                name: t('col-submission-date'),            
                nameAsJsx: (
                    <>
                        <span>
                            {t('col-submission-date1')}
                            <br />
                            {t('col-submission-date2')}
                        </span>
                    </>
                ),
                fieldName: 'submissionDate',
                minWidth: 100,
                maxWidth: 100,
                isResizable: true,
    
                isSorted: true,
                isSortedDescending: true,
                isRowHeader: true,
                data: 'string',
                isPadded: false,
                onRender: (item: ApplicationDto) => {
                    return (
                            <span>
                                <div className="griCellTitleGray">
                                    {UIHelpers.getLocalizedDateTime(
                                        'Date',
                                        item?.submissionDate,
                                        currentLanguage.langIso4
                                    )}
                                </div>
                                <div className="griCellSubTitle">
                                    {UIHelpers.getLocalizedDateTime(
                                        'Time',
                                        item?.submissionDate,
                                        currentLanguage.langIso4
                                    )}
                                </div>
                            </span>
                    );
                },
            },
            {
                key: 'activeFrom',
                name: t('col-active-from'),
                fieldName: 'activeFrom',
                minWidth: 80,
                maxWidth: 80,
                isResizable: true,
    
                isSorted: false,
                isSortedDescending: false,
                isRowHeader: true,
                data: 'string',
                isPadded: false,
                onRender: (item: ApplicationDto) => {
                    return (
                            <span>
                                <div className="griCellTitleGray">
                                    {UIHelpers.getLocalizedDateTime(
                                        'Date',
                                        item?.activeFrom,
                                        currentLanguage.langIso4
                                    )}
                                </div>
                                <div className="griCellSubTitle">
                                    {UIHelpers.getLocalizedDateTime(
                                        'Time',
                                        item?.activeFrom,
                                        currentLanguage.langIso4
                                    )}
                                </div>
                            </span>
                    );
                },
            },
            {
                key: 'activeto',
                name: t('col-active-to'),
                fieldName: 'activeTo',
                minWidth: 80,
                maxWidth: 80,
                isResizable: true,
    
                isSorted: false,
                isSortedDescending: false,
                isRowHeader: true,
                data: 'string',
                isPadded: false,
                onRender: (item: ApplicationDto) => {
                    return ( 
                            <span>
                                <div className="griCellTitleGray">
                                    {UIHelpers.getLocalizedDateTime(
                                        'Date',
                                        item?.activeTo,
                                        currentLanguage.langIso4
                                    )}
                                </div>
                                <div className="griCellSubTitle">
                                    {UIHelpers.getLocalizedDateTime(
                                        'Time',
                                        item?.activeTo,
                                        currentLanguage.langIso4
                                    )}
                                </div>
                            </span>
                    );
                },
            },
            {
                key: 'applicationstatus',
                name: t('applicationstatus'),
                nameAsJsx: (
                    <>
                        <span>
                            {t('statusOf')}
                            <br />
                            {t('application')}
                        </span>
                    </>
                ),
                fieldName: 'applicationstatus',
                minWidth: 100,
                maxWidth: 100,
                isResizable: true,
    
                isSorted: false,
                isSortedDescending: false,
                isRowHeader: true,
    
                onRender: (item: ApplicationDto) => {
                    const [color, text] = getClassNameColorAndText(item?.status);
                    return (
                            <span className={color}>{text}</span>
                    );
                },
            },
            {
                key: 'typeDescription',
                name: t('col-type-description'),
                nameAsJsx: (
                    <>
                        <span>
                            {t('col-type')}
                            {' /'}
                            <br />
                            {t('col-description')}
                        </span>
                    </>
                ),
                fieldName: t('typeDescription'),
                minWidth: 150,
                maxWidth: 150,
                isResizable: false,
    
                isSorted: false,
                isSortedDescending: false,
                isRowHeader: true,
                isPadded: false,
                onRender: (item: ApplicationDto) => {
                    return (
                            <span>
                                {t(item?.type?.toString() || 'None')}
                                <br />
                                {item?.description}
                            </span>
                    );
                },
            },
            {
                key: 'Attachments',
                name: t('col-Attachments'),
                minWidth: 150,
                maxWidth: 150,
                isResizable: false,
    
                isSorted: false,
                isSortedDescending: false,
                isRowHeader: true,
                isPadded: false,
                onRender: (item: ApplicationDto) => {
                    const attachFiles =  item.applicationAttachments ??[];
                    return (
                        <>
                            {(attachFiles?.length ?? 0) > 0 ? (
                                 <div style={{display:'flex', flexDirection:'row',alignItems:'center'}}>
                             <span>{`${attachFiles?.length} ${attachFiles?.length === 1 ? t('file') : t('files')}`}</span>
                                <OverflowSet  role="menubar"
                                            vertical
                                            overflowItems={attachFiles.map( (af,i) => {
                                                const printableSize =  (af.fileSize ?? 0) === 0 ? '' : FileHelpers.bytesToSize(af.fileSize ?? 0);
                                                return ({
                                                    key: `file_${i}`,
                                                    iconProps: {
                                                        iconName: 'DownloadDocument',
                                                    },
                                                    text:af.fileName,
                                                    secondaryText: printableSize,
                                                    onClick: async () => {
                                                        if (af.id && taxpayerId){

                                                            const client = ServiceCreator.getProviderManagementPublicClient();
                                                            const contentBase64 = af.fileContentBase64 ?? await client.getAttachmentContent(taxpayerId,af.id);
                                                            const content = FileHelpers.base64ToArrayBuffer(contentBase64);
                                                            
                                                            UIHelpers.OpenContentInNewTab(content, af.fileType ?? '');
                                                        }
                                                    }
                                                });
                                            })}
                                            onRenderOverflowButton={
                                                onRenderOverflowButton
                                            }
                                            onRenderItem={onRenderItem}
                                        />
                           
                            </div>
                            ): <span>-</span>}
                          
                        </>
                    );
                },
            }
        ];
        setColumns(gridColumns);
    }, [t]);

    return (
        <Dialog
            hidden={hidden}
            onDismiss={() => {
                onClose();
            }}
            maxWidth={850}
            title={(
                <>
                    <Icon iconName='History' /> {t('ApplicationHistory')}
                </>
            )}
        >
            <div className='gridPopup'>
                <DetailsList
                    columns={columns ?? []}
                    items={items}
                    selectionMode={SelectionMode.none}
                    layoutMode={DetailsListLayoutMode.justified}
                    noItemsPlaceholder={<span>{t('common:NoRecords')} </span>}
                    labels={{ resultPerPage: t('common:nbItemPerPage'), totalRecord: t('common:totalRecord') }}
                    paginationSetting={{
                        nbItemPerPage: 10,
                        nbPageShown: 3 /* currentPage: 0, totalPage: 20 when managed externaly */,
                    }}
                />
            </div>
        </Dialog>
    );
};